/**
 * Hook for mapping between app integration IDs and user integration IDs.
 *
 * Provides bidirectional mapping and resolution utilities for working with integrations.
 */

import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { getAppIntegrations, getUserIntegrations } from "@/lib/api/integrations";
import { integrationKeys } from "@/lib/api/query-keys/integrations";
import type { components } from "@/lib/api/types";

type UserIntegration = components["schemas"]["IntegrationResponse"];
type AppIntegration = components["schemas"]["AppIntegrationResponse"];

interface UseIntegrationIdMappingOptions {
	enabled?: boolean;
}

export function useIntegrationIdMapping(options: UseIntegrationIdMappingOptions = {}) {
	const { enabled = true } = options;

	// Fetch user integrations
	const { data: userIntegrationsData } = useQuery<UserIntegration[]>({
		queryKey: integrationKeys.user(),
		queryFn: getUserIntegrations,
		staleTime: 60 * 1000,
		enabled,
	});
	const userIntegrations = enabled ? (userIntegrationsData ?? []) : [];

	// Fetch app integrations (for name resolution)
	const { data: appIntegrations = [] } = useQuery<AppIntegration[]>({
		queryKey: integrationKeys.catalog(),
		queryFn: getAppIntegrations,
		staleTime: 5 * 60 * 1000,
	});

	// Create mapping from app integration ID to user integration ID
	const appToUser = useMemo(() => {
		const map = new Map<number, number>();
		for (const userIntegration of userIntegrations) {
			const appIntegrationId = Number(userIntegration.app_integration_id);
			const userIntegrationId = Number(userIntegration.id);
			if (Number.isFinite(appIntegrationId) && Number.isFinite(userIntegrationId)) {
				map.set(appIntegrationId, userIntegrationId);
			}
		}
		return map;
	}, [userIntegrations]);

	// Create reverse mapping from user integration ID to app integration ID
	const userToApp = useMemo(() => {
		const map = new Map<number, number>();
		for (const userIntegration of userIntegrations) {
			const appIntegrationId = Number(userIntegration.app_integration_id);
			const userIntegrationId = Number(userIntegration.id);
			if (Number.isFinite(appIntegrationId) && Number.isFinite(userIntegrationId)) {
				map.set(userIntegrationId, appIntegrationId);
			}
		}
		return map;
	}, [userIntegrations]);

	// App integration name lookup
	const appIntegrationNameById = useMemo(() => {
		const map = new Map<number, string>();
		for (const appIntegration of appIntegrations) {
			map.set(appIntegration.id, appIntegration.name);
		}
		return map;
	}, [appIntegrations]);

	// User integration lookup
	const userIntegrationById = useMemo(() => {
		const map = new Map<number, UserIntegration>();
		for (const integration of userIntegrations) {
			const integrationId = Number(integration.id);
			if (Number.isFinite(integrationId)) {
				map.set(integrationId, integration);
			}
		}
		return map;
	}, [userIntegrations]);

	/**
	 * Convert user integration IDs to app integration IDs.
	 * Throws error if any integrations cannot be resolved.
	 */
	const resolveAppIntegrationIds = useCallback(
		(userIntegrationIds: number[]) => {
			if (!userIntegrationIds || userIntegrationIds.length === 0) {
				return [];
			}

			const resolvedIds = new Set<number>();
			const missingLabels: string[] = [];

			for (const rawId of userIntegrationIds) {
				const normalizedId = Number(rawId);
				if (!Number.isFinite(normalizedId)) {
					missingLabels.push(`#${rawId}`);
					continue;
				}

				const userIntegration = userIntegrationById.get(normalizedId);
				if (!userIntegration) {
					missingLabels.push(`#${normalizedId}`);
					continue;
				}

				const appIntegrationId = Number(userIntegration.app_integration_id);
				if (!Number.isFinite(appIntegrationId)) {
					missingLabels.push(`#${normalizedId}`);
					continue;
				}

				resolvedIds.add(appIntegrationId);
			}

			if (missingLabels.length > 0) {
				const labels = missingLabels
					.map((label) => {
						const numericId = Number(label.replace("#", ""));
						const integration = Number.isFinite(numericId) && userIntegrationById.get(numericId);

						if (!integration) {
							return label;
						}

						const appIntegrationId = Number(integration.app_integration_id);
						const appIntegrationName = Number.isFinite(appIntegrationId)
							? appIntegrationNameById.get(appIntegrationId)
							: undefined;
						return appIntegrationName ?? label;
					})
					.join(", ");

				throw new Error(
					`We couldn't resolve these integrations: ${labels}. Please reconnect them and try again.`
				);
			}

			return Array.from(resolvedIds);
		},
		[appIntegrationNameById, userIntegrationById]
	);

	/**
	 * Convert app integration IDs to user integration IDs.
	 * Filters out any that cannot be resolved.
	 */
	const resolveUserIntegrationIds = useCallback(
		(appIntegrationIds: number[]) => {
			if (!appIntegrationIds || appIntegrationIds.length === 0) {
				return [];
			}

			return appIntegrationIds
				.map((appId) => appToUser.get(appId))
				.filter((id): id is number => id !== undefined);
		},
		[appToUser]
	);

	return {
		appToUser,
		userToApp,
		resolveAppIntegrationIds,
		resolveUserIntegrationIds,
		userIntegrations,
		appIntegrations,
	};
}
